import {
  ClientOrganizationTagsEnum,
  ClientOrganizationRoleEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { GetOrganizationsInGeoAreaTagsEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { ComponentProps, FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';

import { AddIcon, SearchIcon } from '@/assets';
import { useGetOrganizationById } from '@/features/api/hooks/grapevine-organization/useGetOrganizationById';
import { useGetOrganizationsInGeoArea } from '@/features/api/hooks/grapevine-organization/useGetOrganizationsInGeoArea';
import { useHighestGlobalRole } from '@/features/authorization/provider/useHighestGlobalRole';
import { OrganizationAreaEmptyCard } from '@/features/profile-setting/components/organization-area/OrganizationAreaEmptyCard';
import { OrganizationAreaResultList } from '@/features/profile-setting/components/organization-area/OrganizationAreaResultList';
import { useDescribeOrganizationGeoAreas } from '@/features/profile-setting/components/organization-area/useDescribeOrganizationGeoAreas';
import { useAvailableFilterTags } from '@/features/profile-setting/components/organization-area-filters/useAvailableFilterTags';
import { OrganizationAreaFilters } from '@/features/profile-setting/components/OrganizationAreaFilters';
import { CreateOrganizationModal } from '@/features/profile-setting/components/OrganizationCreationModal';
import { OrganizationSearchModal } from '@/features/profile-setting/components/OrganizationSearchModal';
import { ProfileSettingRoute } from '@/features/profile-setting/navigation/profileSettingRoute';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationBar } from '@/navigation/components/navbar/NavigationBar';
import { StatusBarBackground } from '@/navigation/components/navbar/StatusBarBackground';
import type { AppNativeStackScreenProps } from '@/navigation/types/navigation';
import { GradientBackground } from '@/shared/components/GradientBackground';
import {
  OrganizationAccessGuard,
  OrganizationAccessProvider,
  useOrganizationAccess
} from '@/shared/components/organization';
import { OrganizationAccessState } from '@/shared/components/organization/organizationAccess';
import { StateView } from '@/shared/components/state-view/StateView';
import { isOrganizationType } from '@/shared/utils/organization';

type Props = AppNativeStackScreenProps<
  typeof ProfileSettingRoute.OrganizationsInArea
>;

type ContentProps = {
  organizationId: string;
  navigation: Props['navigation'];
};

const MANAGER_ROLES = [ClientOrganizationRoleEnum.Admin];

const OrganizationAreaContent: FC<ContentProps> = ({
  organizationId,
  navigation
}) => {
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-area'
  });
  const { t: tRoutes } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'routes'
  });
  const { t: tRoles } = useTranslation(LocalizationNameSpace.ORGANIZATION, {
    keyPrefix: 'organization.roleLabels'
  });
  const { t: tGlobalRoles } = useTranslation(
    LocalizationNameSpace.ORGANIZATION,
    {
      keyPrefix: 'organization.globalRoleLabels'
    }
  );

  const globalRole = useHighestGlobalRole();

  const [selectedTags, setSelectedTags] = useState<
    GetOrganizationsInGeoAreaTagsEnum[]
  >([]);
  const [selectedGeoAreaIds, setSelectedGeoAreaIds] = useState<string[]>([]);

  const { data: organization } = useGetOrganizationById(organizationId);

  // TODO: Deduplicate
  const orgGeoAreaIds: string[] = useMemo(() => {
    if (!organization?.geoAreaIds) {
      return [];
    }

    return organization.geoAreaIds.filter(
      (id): id is string => typeof id === 'string' && Boolean(id)
    );
  }, [organization]);
  const effectiveGeoAreaIds =
    selectedGeoAreaIds.length > 0 ? selectedGeoAreaIds : orgGeoAreaIds;

  const {
    data: rawData,
    isLoading,
    refetch,
    isRefetching
  } = useGetOrganizationsInGeoArea({
    tags: selectedTags,
    geoAreaIds: effectiveGeoAreaIds
  });

  // the backend expands the org's geo areas but cannot exclude the reference
  // organization itself (the endpoint has no reference-org parameter)
  const data = useMemo(
    () => rawData?.filter((org) => org.id !== organizationId),
    [rawData, organizationId]
  );

  const availableTags = useAvailableFilterTags(organization);

  const describeOrganizationGeoAreas =
    useDescribeOrganizationGeoAreas(organizationId);

  const isTerritorialAuthority = isOrganizationType(
    ClientOrganizationTagsEnum.TerritorialAuthority,
    organization
  );
  const isAdminOrganization = isOrganizationType(
    ClientOrganizationTagsEnum.Admin,
    organization
  );
  const canManageArea = isTerritorialAuthority || isAdminOrganization;
  const canManage =
    useOrganizationAccess({ roles: MANAGER_ROLES }) ===
    OrganizationAccessState.Granted;

  const [isCreationOpen, setIsCreationOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const hasActiveFilters =
    selectedTags.length > 0 || selectedGeoAreaIds.length > 0;

  const handleResetFilters = useCallback(() => {
    setSelectedTags([]);
    setSelectedGeoAreaIds([]);
  }, []);

  const handleOrganizationPress = useCallback(
    (orgId: string) => {
      navigation.navigate(ProfileSettingRoute.OrganizationDetail, {
        id: orgId
      });
    },
    [navigation]
  );

  const handleCloseCreation = useCallback(() => {
    setIsCreationOpen(false);
  }, []);

  const handleOpenCreation = useCallback(() => {
    setIsCreationOpen(true);
  }, []);

  const handleCloseSearch = useCallback(() => {
    setIsSearchOpen(false);
  }, []);

  useEffect(() => {
    let buttons: ComponentProps<typeof NavigationBar>['buttons'];

    if (canManageArea) {
      buttons = [
        {
          name: 'search',
          icon: SearchIcon,
          onPress: () => {
            setIsSearchOpen(true);
          }
        }
      ];

      if (canManage) {
        buttons.push({
          name: 'create',
          icon: AddIcon,
          onPress: () => {
            setIsCreationOpen(true);
          }
        });
      }
    }

    const role = organization?.role;
    const directRoleLabel = role ? tRoles(role) : undefined;
    const roleLabel = globalRole ? tGlobalRoles(globalRole) : directRoleLabel;
    const subTitle =
      [organization?.name, roleLabel].filter(Boolean).join(' · ') || undefined;

    navigation.setOptions({
      header: () => (
        <StatusBarBackground>
          <NavigationBar
            title={tRoutes('organization-area')}
            subTitle={subTitle}
            buttons={buttons}
            balanceStartButtons={false}
          />
        </StatusBarBackground>
      )
    });
  }, [
    navigation,
    canManage,
    canManageArea,
    globalRole,
    organization,
    tRoles,
    tGlobalRoles,
    tRoutes
  ]);

  return (
    <GradientBackground>
      <StateView
        isLoading={isLoading}
        isRefetching={isRefetching}
        onRefetch={refetch}
        stateLabels={t('state', { returnObjects: true })}
        backgroundColor="transparent"
        contentContainerStyle={styles.content}
      >
        <View style={styles.header}>
          <Text variant="titleLarge">
            {t('title', { count: data?.length ?? 0 })}
          </Text>
        </View>

        <OrganizationAccessGuard
          requiredTagsAny={[
            ClientOrganizationTagsEnum.TerritorialAuthority,
            ClientOrganizationTagsEnum.Admin
          ]}
          ignoreRoles
        >
          <OrganizationAreaFilters
            availableTags={availableTags}
            selectedTags={selectedTags}
            onChangeTags={setSelectedTags}
            organizationId={organizationId}
            selectedGeoAreaIds={selectedGeoAreaIds}
            onChangeGeoAreaIds={setSelectedGeoAreaIds}
          />
          {data && data.length > 0 && (
            <OrganizationAreaResultList
              organizations={data}
              describeOrganization={describeOrganizationGeoAreas}
              onOrganizationPress={handleOrganizationPress}
            />
          )}
          {data && data.length === 0 && !isLoading && (
            <OrganizationAreaEmptyCard
              hasActiveFilters={hasActiveFilters}
              onResetFilters={handleResetFilters}
              onCreatePress={handleOpenCreation}
            />
          )}
        </OrganizationAccessGuard>
        <CreateOrganizationModal
          organizationId={organizationId}
          visible={isCreationOpen}
          onRequestClose={handleCloseCreation}
          onDismiss={handleCloseCreation}
        />
        <OrganizationSearchModal
          visible={isSearchOpen}
          organizations={data}
          describeOrganization={describeOrganizationGeoAreas}
          onSelect={handleOrganizationPress}
          onCreatePress={handleOpenCreation}
          onDismiss={handleCloseSearch}
        />
      </StateView>
    </GradientBackground>
  );
};

export const OrganizationAreaScreen: FC<Props> = ({ route, navigation }) => {
  const { organizationId } = route.params;
  const { data: organization } = useGetOrganizationById(organizationId);

  return (
    <OrganizationAccessProvider organization={organization}>
      <OrganizationAreaContent
        organizationId={organizationId}
        navigation={navigation}
      />
    </OrganizationAccessProvider>
  );
};

const styles = StyleSheet.create({
  content: { paddingBottom: 32 },
  header: { paddingHorizontal: 16, paddingTop: 32, paddingBottom: 12 }
});
